import { apiSlice } from '@/features/api/apiSlice';

export type OrganizationDetails = {
  id: string;
  slug: string;
  name: string;
  planCode: 'free' | 'starter' | 'pro' | 'enterprise';
  trialEndsAt: string | null;
  suspendedAt: string | null;
  createdAt: string;
  counts: {
    templates: number;
    runningExperiments: number;
    activeAutomations: number;
    members: number;
  };
};

export const NOTIFICATION_EVENTS = [
  'send.failed',
  'send.bounced',
  'send.complained',
  'deliverability.degraded',
  'ab.experiment.complete',
  'billing.usage.threshold',
  'integration.disconnected',
  'member.invited',
] as const;
export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

export const NOTIFICATION_CHANNELS = ['email', 'slack', 'webhook', 'inapp'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export type NotificationMatrix = Partial<
  Record<NotificationEvent, Partial<Record<NotificationChannel, boolean>>>
>;

export type NotificationChannelConfig = {
  slackWebhookUrl?: string | null;
  slackChannel?: string | null;
  outboundWebhookUrl?: string | null;
  digestEmail?: string | null;
};

export type NotificationsPayload = {
  prefs: NotificationMatrix;
  channels: NotificationChannelConfig;
};

export const organizationsApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    getCurrentOrg: b.query<OrganizationDetails, void>({
      query: () => '/organizations/me',
      providesTags: ['Me'],
    }),
    updateOrg: b.mutation<{ id: string; slug: string; name: string }, { name?: string; slug?: string }>({
      query: (body) => ({ url: '/organizations/me', method: 'PATCH', body }),
      invalidatesTags: ['Me'],
    }),
    deleteOrg: b.mutation<{ ok: true }, void>({
      query: () => ({ url: '/organizations/me', method: 'DELETE' }),
      invalidatesTags: ['Me'],
    }),
    getNotifications: b.query<NotificationsPayload, void>({
      query: () => '/organizations/me/notifications',
      providesTags: ['Me'],
    }),
    updateNotifications: b.mutation<
      NotificationsPayload,
      { prefs?: NotificationMatrix; channels?: NotificationChannelConfig }
    >({
      query: (body) => ({ url: '/organizations/me/notifications', method: 'PUT', body }),
      invalidatesTags: ['Me'],
    }),
  }),
});

export const {
  useGetCurrentOrgQuery,
  useUpdateOrgMutation,
  useDeleteOrgMutation,
  useGetNotificationsQuery,
  useUpdateNotificationsMutation,
} = organizationsApi;
